import { createSelector } from '@reduxjs/toolkit';
import set from 'lodash/fp/set';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';

import {
  ADD_BASKET_RESPONSE,
  UNDONATE_BASKET_RESPONSE,
  DONATE_BASKET_RESPONSE,
} from 'actions/baskets';
import { EMPTY_ARRAY, EMPTY_OBJECT } from 'lib/constants';
import {
  CHANGE_MEAL_OPTION_V3,
  UNDO_MEAL_OPTIONS_V3,
  SET_EDITED_BASKET_V3,
  CLEAR_EDITED_BASKET_V3,
  UNDO_BASKET_EDITS_V3,
  REMOVE_MEAL_V3,
  UPDATE_BASKET_V3_RESPONSE,
  REMOVE_ALL_MEALS_V3,
} from 'lib/constants/v3Actions';

export const setMealOptions = (mealOptionSelections, mealId) => {
  const selections = get(mealOptionSelections, mealId, EMPTY_ARRAY);

  if (selections.length === 0) return EMPTY_OBJECT;

  return selections.reduce(
    (hash, option) => ({
      ...hash,
      [option.mealOptionGroupId]: option,
    }),
    {}
  );
};

export const updateBasket = ({ meals, mealOptionSelections }) => {
  if (!meals) return EMPTY_OBJECT;

  return meals.reduce((hash, meal) => {
    const options = setMealOptions(mealOptionSelections, meal.id);

    if (options === EMPTY_OBJECT) return hash;

    return {
      ...hash,
      [meal.id]: options,
    };
  }, EMPTY_OBJECT);
};

const initialState = {};

export default (state = initialState, action) => {
  switch (action.type) {
    case ADD_BASKET_RESPONSE:
    case UNDONATE_BASKET_RESPONSE:
    case UPDATE_BASKET_V3_RESPONSE:
    case DONATE_BASKET_RESPONSE:
      return updateBasket(action.response.weeklyBasket);
    case UNDO_BASKET_EDITS_V3:
    case SET_EDITED_BASKET_V3:
    case REMOVE_ALL_MEALS_V3:
      return updateBasket(action.basket);
    case CHANGE_MEAL_OPTION_V3:
      return set([action.mealId, action.mealOptionGroupId], action.mealOption, state);
    case UNDO_MEAL_OPTIONS_V3: {
      if (isEmpty(action.mealOptions)) {
        return state[action.mealId] ? omit(state, action.mealId) : state;
      }
      return set(action.mealId, action.mealOptions, state);
    }
    case CLEAR_EDITED_BASKET_V3:
      return initialState;
    case REMOVE_MEAL_V3:
      return omit(state, action.mealId);
    default:
      return state;
  }
};

// Selector Logic
const getMealOptionsState = state => state || EMPTY_OBJECT;
const getMealId = (state, mealId) => mealId;

export const selectMealOptionsForMeal = createSelector(
  [getMealOptionsState, getMealId],
  (mealOptions, mealId) => {
    return get(mealOptions, mealId, EMPTY_OBJECT);
  }
);
